import { useCallback, useState } from "react";
import { IReport } from "../../helpers/Interfaces";
import { convertDuration, getPriority, validDateField, validStringField } from "../../helpers/HelperFunctions";
import { Checkbox } from '@mui/material';
import { Colors } from "../../helpers/Colors";
import SearchableDown from "../../components/DropDown/DropDown";
import RemoveFilter from "../../assets/images/RemoveFilter.svg";
import BlueHomeDot from "../../assets/images/BlueHomeDot.svg";
import BlueHomeDotDisable from "../../assets/images/BlueHomeDotDisable.svg";
import ArrowRightIcon from "../../assets/images/ArrowRight.svg";
import { ReportFilterTypes } from "../../helpers/Types";
import Trash from "../../assets/images/Trash.svg";
import AccordionOpen from "../../assets/images/AccordionOpenBlack.svg";

type ReportsUiProps = {
    reports: IReport[];
    setSelectedReport: CallableFunction;
    openView: CallableFunction;
    filter: (filterBy: ReportFilterTypes, query: string) => void;
    showFilter: boolean;
    cancelFilter: CallableFunction;
    showDelete: boolean;
    tickReport: (reportId: string) => void;
    toggleAllReports: (reportIds: string[]) => void;
    isReportTicked: (reportId: string) => boolean;
    areAllReportsTicked: (reportIds: string[]) => boolean;
    searchReports: CallableFunction;
    selectedDateFilter: "createdDate" | "dueDate" | "submittedDate" | "completedDate";
    handleDateClick: (selectedDateFilter: "createdDate" | "dueDate" | "submittedDate" | "completedDate") => void;
    filterState: "ASCENDING" | "DESCENDING" | null;
}

export default function ReportsUI(props: Readonly<ReportsUiProps>) {

    const [selectedFilter, setSelectedFilter] = useState<ReportFilterTypes>("patientName");

    function getInitialValues() {
        let valueArr = [] as any[];
        props.reports.forEach(report => {
            if (report[selectedFilter] != null) {
                valueArr.push(report[selectedFilter])
            }
            else {
                valueArr.push('-')
            }
        });

        let distinctValues = valueArr.filter((value, index, array) => array.indexOf(value) === index);
        return distinctValues;
    }

    const handleSelectedItem = (selectedValue: string) => {
        props.filter(selectedFilter, selectedValue);
    };

    const handleSearchOpen = (filterBy: ReportFilterTypes) => {
        setSelectedFilter(filterBy);
    };

    const handleClick = useCallback((selectedReport: IReport) => {
        props.setSelectedReport(selectedReport);
        props.openView("view");
    }, []);

    return (<div className="table">
        <div className="header">
            <div className="header-row extra-small">
                <Checkbox icon={<img alt='check box' src={BlueHomeDotDisable} />}
                    checkedIcon={<img alt='check' src={BlueHomeDot} />}
                    value="cryon" style={{ color: Colors.white_10 }}
                    checked={props.areAllReportsTicked(props.reports.map(report => report.id))}
                    onClick={(e) => { props.toggleAllReports(props.reports.map(report => report.id)); e.stopPropagation(); }}
                />
            </div>
            <div className="header-row medium">
                <span className="text">Patient Name</span>
                <SearchableDown searchable={true} initialDetails={getInitialValues()}
                    onSelect={(selectedValue: string) => { handleSelectedItem(selectedValue); }}
                    setSelectedFilter={() => { handleSearchOpen("patientName") }} />
            </div>
            <div className="header-row medium">
                <span className="text">Doctor</span>
                <SearchableDown searchable={true} initialDetails={getInitialValues()}
                    onSelect={(selectedValue: string) => { handleSelectedItem(selectedValue); }}
                    setSelectedFilter={() => { handleSearchOpen("doctorName") }} />
            </div>
              <div className="header-row medium">
                <span className="text">Stage</span>
            </div>
              <div className="header-row medium">
                <span className="text">Status</span>
            </div>
            <div className="header-row medium clickable" onClick={() => props.handleDateClick("createdDate")}>
                <span className="text">Created Date</span>
                {props.selectedDateFilter === "createdDate" && props.filterState !== null
                    ? <img src={AccordionOpen} alt="SortArrow" className={props.filterState === "ASCENDING" ? "image" : "invert-image"} />
                    : <span className="text image">-</span>
                }
            </div>
            <div className="header-row medium clickable" onClick={() => props.handleDateClick("dueDate")}>
                <span className="text">Due Date</span>
                {props.selectedDateFilter === "dueDate" && props.filterState !== null
                    ? <img src={AccordionOpen} alt="SortArrow" className={props.filterState === "ASCENDING" ? "image" : "invert-image"} />
                    : <span className="text image">-</span>
                }
            </div>
            <div className="header-row medium clickable" onClick={() => props.handleDateClick("submittedDate")}>
                <span className="text">Submitted Date</span>
                {props.selectedDateFilter === "submittedDate" && props.filterState !== null
                    ? <img src={AccordionOpen} alt="SortArrow" className={props.filterState === "ASCENDING" ? "image" : "invert-image"} />
                    : <span className="text image">-</span>
                }
            </div>
            <div className="header-row medium clickable" onClick={() => props.handleDateClick("completedDate")}>
                <span className="text">Completed Date</span>
                {props.selectedDateFilter === "completedDate" && props.filterState !== null
                    ? <img src={AccordionOpen} alt="SortArrow" className={props.filterState === "ASCENDING" ? "image" : "invert-image"} />
                    : <span className="text image">-</span>
                }
            </div>
            <div className="header-row small">
                <span className="text">Duration</span>
            </div>
            <div className="header-row small">
                <span className="text">Urgency</span>
                <SearchableDown searchable={false} initialDetails={getInitialValues()}
                    onSelect={(selectedValue: string) => { handleSelectedItem(selectedValue); }}
                    setSelectedFilter={() => { handleSearchOpen("priority") }} />
            </div>
            <div className="header-row extra-small justify-end gap">
                {props.showFilter
                    ? <img className="filter-icon" src={RemoveFilter} alt={"RemoveFilter"} onClick={() => { props.cancelFilter(); }} />
                    : <div className="filter-icon" />
                }
                {props.showDelete
                    ? <img className="trash-icon" src={Trash} alt={"Trash"} onClick={() => { props.openView("bulkDelete"); }} />
                    : <div className="filter-icon" />
                }
            </div>
        </div>
        <div className="data">
            {props.reports.map((report) => {
                return (<div key={report.id} className="data-row pending-padding" onClick={() => { handleClick(report); }}>
                    <div className="cell text extra-small" >
                        <Checkbox icon={<img alt='check box' src={BlueHomeDotDisable} />}
                            checkedIcon={<img alt='check' src={BlueHomeDot} />}
                            value="cryon" style={{ color: Colors.white_10 }}
                            checked={props.isReportTicked(report.id)}
                            onClick={(e) => {
                                props.tickReport(report.id); e.stopPropagation();
                            }} />
                    </div>
                    <div className="cell text medium">{validStringField(report.patientName)}</div>
                    <div className="cell text medium">{validStringField(report.doctorName)}</div>
                    <div className="cell text medium">{validStringField(report.stage)}</div>
                    <div className="cell text medium">{validStringField(report.documentStatus)}</div>
                    <div className="cell text medium"><span>{validDateField(report.createdDate)}</span></div>
                    <div className="cell text medium"><span>{validDateField(report.dueDate)}</span></div>
                    <div className="cell text medium"><span>{validDateField(report.submittedDate)}</span></div>
                    <div className="cell text medium"><span>{validDateField(report.completedDate)}</span></div>
                    <div className="cell text small">{convertDuration(report.totalDuration)}</div>
                    <div className="cell text small justify">{getPriority(report.priority)}</div>
                    <div className="cell text extra-small justify-end">
                        <img className="icon" src={ArrowRightIcon} alt={"ArrowRight"} />
                    </div>
                </div>)
            })}
        </div>
    </div>)
}